import { Link } from "../lib/router";
import { PLATFORMS, getPlatformInfo } from "../lib/social";
import { PlatformIcon } from "../components/PlatformIcon";
import { RevealSection } from "../components/LandingAnimations";

const FEATURES = [
  {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />,
    title: "Multi-Platform Publishing",
    description: "Publish to 9 social platforms simultaneously from a single editor. Write once and reach every audience.",
    bullets: ["Simultaneous posting", "Platform-specific formatting", "Media auto-optimization"],
  },
  {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
    title: "Smart Scheduling",
    description: "Queue posts for optimal engagement times. Set it and forget it with intelligent scheduling.",
    bullets: ["Time zone aware", "Best-time suggestions", "Queue management"],
    comingSoon: true,
  },
  {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
    title: "Analytics Dashboard",
    description: "Unified metrics across all platforms with interactive charts. Track reach, engagement, followers, and more.",
    bullets: ["Page-level insights", "Per-post analytics", "Custom date ranges"],
  },
  {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />,
    title: "Account Management",
    description: "Connect and manage all your social accounts from one dashboard. OAuth-based secure connections.",
    bullets: ["One-click OAuth connect", "Multi-account per platform", "Connection health monitoring"],
  },
  {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />,
    title: "Media Support",
    description: "Upload images and videos with automatic optimization per platform. Alt text and caption support.",
    bullets: ["Multi-image posts", "Video uploads", "Alt text for accessibility"],
  },
  {
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />,
    title: "API Access",
    description: "Full REST API with project-scoped API keys. Automate publishing or integrate into your workflows.",
    bullets: ["RESTful endpoints", "Scoped API keys", "Webhook support"],
  },
];

const PLATFORM_FEATURES: Record<string, string[]> = {
  twitter: ["Text posts", "Images", "Threads"],
  instagram: ["Photos", "Reels", "Carousels", "Insights"],
  facebook: ["Page posts", "Photos", "Videos", "Insights"],
  linkedin: ["Text posts", "Articles", "Images"],
  tiktok: ["Videos", "Photos"],
  youtube: ["Video uploads", "Shorts"],
  reddit: ["Text posts", "Links", "Images"],
  pinterest: ["Pins", "Images"],
  telegram: ["Messages", "Photos"],
};

export function Features() {
  return (
    <>
      {/* Hero */}
      <section className="pt-20 pb-16 px-4 text-center">
        <div className="animate-fade-up">
          <span className="inline-block px-3 py-1 rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold border border-orange-200/60 dark:border-orange-500/20 mb-6">
            Features
          </span>
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-4 animate-fade-up" style={{ animationDelay: "0.1s" }}>
          Built for modern <span className="text-gradient">social teams</span>
        </h1>
        <p className="text-lg text-stone-500 dark:text-zinc-400 max-w-xl mx-auto animate-fade-up" style={{ animationDelay: "0.2s" }}>
          Every feature you need to manage, publish, and analyze your social presence across 9 platforms.
        </p>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {FEATURES.map((f, i) => (
            <RevealSection key={i}>
              <div className="p-6 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full relative">
                {f.comingSoon && (
                  <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[10px] font-semibold uppercase">
                    Coming Soon
                  </span>
                )}
                <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-500/10 text-orange-500 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">{f.icon}</svg>
                </div>
                <h3 className="text-base font-bold text-stone-900 dark:text-white mb-2">{f.title}</h3>
                <p className="text-sm text-stone-500 dark:text-zinc-400 leading-relaxed mb-4">{f.description}</p>
                <ul className="space-y-1.5">
                  {f.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2 text-xs text-stone-600 dark:text-zinc-300">
                      <span className="w-4 h-4 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-[10px]">&#10003;</span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </RevealSection>
          ))}
        </div>
      </section>

      {/* Platform Capabilities */}
      <section className="py-16 bg-stone-50 dark:bg-zinc-900/50 px-4">
        <RevealSection className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-stone-900 dark:text-white tracking-tight">
            Platform <span className="text-gradient">capabilities</span>
          </h2>
          <p className="text-stone-500 dark:text-zinc-400 mt-3 max-w-md mx-auto text-sm">
            See what each platform supports out of the box.
          </p>
        </RevealSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {PLATFORMS.map((p) => {
            const info = getPlatformInfo(p.id);
            const features = PLATFORM_FEATURES[p.id] || [];
            return (
              <RevealSection key={p.id}>
                <div className="p-4 rounded-xl border border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 hover:border-orange-300 dark:hover:border-orange-500/30 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`w-9 h-9 ${info.color} text-white rounded-xl flex items-center justify-center`}>
                      <PlatformIcon platform={p.id} className="w-4.5 h-4.5" />
                    </span>
                    <span className="font-semibold text-stone-900 dark:text-white text-sm">{p.name}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {features.map((f) => (
                      <span key={f} className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-zinc-800 text-stone-600 dark:text-zinc-400 text-[11px] font-medium">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </RevealSection>
            );
          })}
        </div>
      </section>

      {/* API Mockup */}
      <section className="py-24 px-4">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-12">
          <RevealSection direction="left" className="md:w-1/2">
            <h2 className="text-3xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-4">
              Developer-first <span className="text-gradient">API</span>
            </h2>
            <p className="text-stone-500 dark:text-zinc-400 leading-relaxed mb-6">
              Integrate SocialCast into your existing tools with our REST API. Create posts, manage accounts, and fetch analytics programmatically.
            </p>
            <Link href="/register" className="inline-block px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-all">
              Get API Access
            </Link>
          </RevealSection>
          <RevealSection direction="right" className="md:w-1/2">
            <div className="rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 bg-stone-900 dark:bg-zinc-950 p-5 font-mono text-sm overflow-x-auto">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-3 h-3 rounded-full bg-red-400" />
                <span className="w-3 h-3 rounded-full bg-amber-400" />
                <span className="w-3 h-3 rounded-full bg-green-400" />
                <span className="text-stone-500 text-xs ml-2">API Request</span>
              </div>
              <pre className="text-green-400 whitespace-pre-wrap leading-relaxed">
{`curl -X POST /social/posts \\
  -H "X-API-Key: sk_live_..." \\
  -d '{
    "text": "Hello world!",
    "account_ids": [1, 2, 3],
    "media_urls": [{
      "url": "https://...",
      "type": "image"
    }]
  }'`}
              </pre>
              <div className="mt-4 pt-4 border-t border-stone-700">
                <pre className="text-blue-400 whitespace-pre-wrap leading-relaxed">
{`{
  "success": true,
  "data": {
    "id": 42,
    "status": "published",
    "successful_count": 3
  }
}`}
                </pre>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/[0.06] to-transparent dark:from-orange-500/[0.04]" />
        <RevealSection className="relative text-center max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-4">
            Start publishing smarter today
          </h2>
          <p className="text-stone-500 dark:text-zinc-400 mb-8">
            Free to start. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register" className="px-8 py-4 bg-orange-500 text-white rounded-2xl font-semibold text-lg hover:bg-orange-600 transition-all animate-pulse-glow shadow-lg shadow-orange-500/20">
              Get Started Free
            </Link>
            <Link href="/pricing" className="px-8 py-4 bg-white dark:bg-zinc-800 text-stone-700 dark:text-zinc-200 rounded-2xl font-semibold text-lg border border-stone-200 dark:border-zinc-700 hover:bg-stone-50 dark:hover:bg-zinc-750 transition-colors">
              View Pricing
            </Link>
          </div>
        </RevealSection>
      </section>
    </>
  );
}
